import React, { useEffect, useState } from "react";
import {
  MerchantAccount,
  SellerGridModel,
  GeneralSettingsModel,
  AddressModel,
  PaymentSettings,
  PaymentMethodDetails,
} from "../../../shared/models";
import VisaLogo from "../../../../../../images/payment/visa-card_logo.svg";
import MasterCardLogo from "../../../../../../images/payment/master-card-black-background.svg";
import DiscoverLogo from "../../../../../../images/payment/discover-icon.svg";
import AmexLogo from "../../../../../../images/payment/amex_card_logo.svg";
import ApplePayLogo from "../../../../../../images/payment/apple-pay-icon.svg";
import GooglePayLogo from "../../../../../../images/payment/gpay-icon.svg";
import BankIcon from "../../../../../../images/payment/ach-bank-with-caption.svg";
import CheckWireIcon from "../../../../../../images/payment/check-wire-card.svg";
import PaymentCardIcon from "../../../../../../images/payment/card-icon.svg";
import NewSellerPopup from "./NewSellerPopup";
import DeleteSellerPopup from "./DeleteSellerPopup";
import GeneralSettings from "./GeneralSettings";
import PaymentSettingsService from "../PaymentSettingsService";
import CardIcon from "../../../../../../images/sidebar/merchant-accounts-icon-blue.svg";
import EditIcon from "../../../../../../images/products/edit-blue.svg";
import DeleteIcon from "../../../../../../images/products/trash-blue.svg";
import { observer } from "mobx-react";
import {
  ActiveInactiveButton,
  AuthService,
  Collapsible,
  CollapsiblePanel,
  CustomLabel,
  EditableField,
  FlexContainer,
  InfoIcon,
  Input,
  ITableHeaders,
  Loader,
  SapphireBlueButton,
  TableContainer,
  TableWithHeaderButtons,
  ToastService,
  ToggleSwitch,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import {
  AccountSettingsStore,
  CountryStore,
  ItemsStore,
  RolesAndPermissionStore,
} from "../../../shared/store";
import {
  MerchantAccountService,
  SellerService,
} from "../../../shared/services";
import { getMerchantAccountStore } from "../../merchantAccounts/MerchantAccountStore";
import { IAPIUpdateAccountSettingsModel } from "../../../shared/interfaces";
import { ApplicationStatus } from "../../../shared/enums";
import {
  DescriptionCell,
  ItemDetailMainWrapper,
  LabelCell,
  MainOuterCard,
  VariantWrapper,
} from "../../../shared/styles";
import Categories from "./Categories";
import { TableGrid } from "../../../shared/components";
import Beneficiaries from "./Beneficiaries";
import styled from "styled-components";
import { firstValueFrom } from "rxjs";
import PromotionsGrid from "./PromotionsGrid";
import { PromotionGridModel } from "../../../shared/models/PromotionModels";
import PromotionService from "../../../shared/services/PromotionService";

type IProps = {
  itemStore: ItemsStore;
  accountId: string;
  rolesAndPermissionStore: RolesAndPermissionStore;
};
interface IEditProps {
  margin?: string;
}

/* `keyof PaymentSettings` would also include methods like `createAPIModel`,
   which resolves indexed access (e.g. item.paymentSettings[type]) to a union
   that includes a function type. This type restricts callers to the actual
   payment-method properties. Only "cards" (Credit Card) is wired up today. */
type PaymentMethodKey =
  "cards" | "applePay" | "googlePay" | "bankAch" | "checkWire";

const BasicPlaceholder = styled.div`
  height: 300px;
  width: 100%;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 30px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  ${CustomLabel} {
    font-size: 22px;
    color: #4c4c4c;
  }
`;
const FlexTable = styled.div<IEditProps>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 700px;
  margin: ${(props) => props.margin || "0px"};
  .custom-toggle-switch {
    .react-switch-bg {
      width: 28px !important;
      height: 10px;
    }
    .react-switch-handle {
      height: 20px !important;
      width: 20px !important;
      top: 3px !important;
    }
  }

  &.justify-center {
    justify-content: center;
  }
  &.pd-height-static {
    min-height: 41px;
  }
  .payment-method-icon {
    height: 15px;
    width: 19px;
    margin-left: 15px;
  }
  .bank-ach-icon {
    img {
      max-width: 55px;
      height: 45px;
    }
  }
  .check-wire-icon {
    img {
      min-width: 36px;
      max-width: 40px;
      height: 22px;
    }
  }
`;
const FlexTableDescription = styled.div`
  width: 70%;
`;
const DetailPanel = styled.div`
  padding: 20px;
`;

const PaymentLabel = styled.span`
  font-size: 16px;
  margin: 0px 23px 0px 15px;
  width: 94px;
  color: #555;
`;

const PaymentIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-grow: 1;

  img {
    min-width: 35px;
    max-width: 40px;
    height: 23px;
  }
`;

/* ----------------- Payment Details / Surcharge (Default) UI ---------------- */

const MerchantCard = styled.div`
  width: 100%;
  background-color: #fff;
  border: 1px solid #e4e9f2;
  border-radius: 10px;
  margin-bottom: 20px;
`;

const MerchantCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 16px 20px;
  cursor: pointer;

  .collapsed-header-icon {
    height: 22px;
    width: 32px;
  }
  i {
    margin-left: auto;
    font-size: 16px;
    color: #7a8699;
  }
`;

const DefaultBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: #556bb8;
  background-color: #eef1fb;
  border-radius: 4px;
  padding: 3px 8px;
  white-space: nowrap;
`;

const MerchantCardBody = styled.div`
  padding: 18px 20px 20px;
  border-top: 1px solid #eef0f6;
`;

const SurchargeCard = styled.div`
  width: 100%;
  background-color: #fbfcff;
  border: 1px solid #e4e9f2;
  border-radius: 10px;
  padding: 18px 20px;
  margin-top: 20px;

  .surcharge-chkbox {
    .form-check-input {
      height: 18px;
      width: 18px;
      background-size: 18px;
      margin-right: 8px;
      border-color: #c3cad8;
    }
    label {
      font-size: 15px;
      color: #3d4a5c;
      white-space: nowrap;
    }
  }
`;

const SurchargeNumberField = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: stretch;
  border: 1px solid #c3cad8;
  border-radius: 6px;
  overflow: hidden;
  background-color: ${(props) => (props.$disabled ? "#f4f6fa" : "#fff")};

  &.has-error {
    border-color: #f00;
  }

  .input-wrapper input {
    width: 62px;
    height: 32px;
    border: none;
    border-radius: 0px;
    padding: 0px 8px;
    font-size: 15px;
    color: #3d4a5c;
    background-color: transparent;
    text-align: left;
  }
`;

const SurchargeStepper = styled.div`
  display: flex;
  flex-direction: column;
  width: 22px;
  border-left: 1px solid #dde2ec;

  i {
    flex: 1 1 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    color: #7a8699;
    cursor: pointer;

    &:first-child {
      border-bottom: 1px solid #dde2ec;
    }
    &.not-allowed {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
`;

const SurchargeValidationNote = styled.div<{ $hasError?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 1px;
  margin-top: 10px;
  i {
    font-size: 14px;
    /* Green matches the surcharge checkboxes; red takes over once the entered
       percentage is out of range. */
    color: ${(props) => (props.$hasError ? "#f00" : "#57ac2d")};
  }
  label {
    font-size: 14px;
    color: ${(props) => (props.$hasError ? "#f00" : "#7a8699")};
  }
`;

const NoteText = styled.p`
  font-size: 14px;
  color: #555;
  margin-top: 10px;
  font-style: italic;
  font-weight: 400;
`;
const PaySettingsPaymentMethods: React.FC<IProps> = ({
  itemStore,
  accountId,
  rolesAndPermissionStore,
}) => {
  const [data, setData] = useState<MerchantAccount[]>([]);
  const [showLoader, setShowLoader] = React.useState(true);
  const [openPaymentDetailsBlock, setOpenPaymentDetailsBlock] = useState(false);
  const [sellerData, setSellerData] = useState<SellerGridModel[]>([]);
  const [promotionsData, setPromotionsData] = useState<PromotionGridModel[]>(
    [],
  );
  const [openPaymentDetailsIndexes, setOpenPaymentDetailsIndexes] = useState<
    number[]
  >([]);
  const [generalSettings, setGeneralSettings] = useState(
    AccountSettingsStore.generalSettings,
  );
  const [sellerPopup, setSellerPopup] = useState({
    showEdit: false,
    showNew: false,
    sellerId: "",
    showDelete: false,
  });

  useEffect(() => {
    setGeneralSettings(AccountSettingsStore.generalSettings);
  }, [AccountSettingsStore.generalSettings]);

  useEffect(() => {
    getMerchantAccountsData();
    getSellerDetails();
    getPromotionDetails();
    if (AccountSettingsStore.isLoadingInProgress) {
      AccountSettingsStore.loadGeneralSettings();
    }
  }, []);

  async function getMerchantAccountsData() {
    setShowLoader(true);
    const response = await firstValueFrom(
      MerchantAccountService.getMerchantAccounts(accountId, 1, 99999, ""),
    );
    if (response.result) {
      setShowLoader(false);
      const deserializedData = response.result
        .filter((item) => item.applicationStatus === ApplicationStatus.Active)
        .map((item) => MerchantAccount.deserializeAPIModel(item));
      setData(deserializedData);
    } else {
      setShowLoader(false);
      ToastService.showError("Error in getting merchant accounts data");
    }
  }

  const getSellerDetails = async () => {
    setShowLoader(true);
    const response = await firstValueFrom(
      SellerService.getSellersGridData(
        AuthService.authData.accountId,
        1,
        "99999",
        "",
      ),
    );
    if (response.result) {
      setShowLoader(false);
      const deserializedData = response.result.map((item) =>
        SellerGridModel.deserializeAPIModel(item),
      );
      setSellerData(deserializedData);
    } else {
      setShowLoader(false);
      ToastService.showError("Error in getting seller details");
    }
  };

  const getPromotionDetails = async () => {
    setShowLoader(true);
    const response = await firstValueFrom(
      PromotionService.getPromotionsGridData(
        AuthService.authData.accountId,
        1,
        "99999",
        "",
      ),
    );
    if (response.result) {
      setShowLoader(false);
      const deserializedData = response.result.map((item) =>
        PromotionGridModel.deserializeAPIModel(item),
      );
      setPromotionsData(deserializedData);
    } else {
      setShowLoader(false);
      ToastService.showError("Error in getting promotion details");
    }
  };

  function onClickSave() {
    if (!rolesAndPermissionStore.hasManagePermission("Save")) return;
    const isValid = generalSettings.validate();
    let hasPaymentErrors = false;

    const updatedData = data.map((account) => {
      const isValid = account.validate();
      if (!isValid) hasPaymentErrors = true;
      return new MerchantAccount({ ...account });
    });

    if (!isValid) {
      setGeneralSettings(new GeneralSettingsModel({ ...generalSettings }));
      ToastService.showError("Please enter valid data in General Section");
      return;
    }

    setData(updatedData);

    if (hasPaymentErrors) {
      setOpenPaymentDetailsBlock(true);
      const openIndexes: number[] = [];

      const updatedData = data.map((account, index) => {
        const isValid = account.validate();
        if (!isValid) {
          hasPaymentErrors = true;
          openIndexes.push(index);
        }
        return new MerchantAccount({ ...account });
      });
      setData(updatedData);
      setOpenPaymentDetailsIndexes(openIndexes);
      ToastService.showError(PaymentMethodDetails.surchargeRangeMessage);
      return;
    }

    const apiModel: IAPIUpdateAccountSettingsModel = {
      accountId: AuthService.authData.accountId,
      paymentSettings: {
        accountId: AuthService.authData.accountId,
        paymentSettings: data.map((account) =>
          account.createPaymentSettingsAPIModel(),
        ),
      },
      generalSettings: generalSettings.createApiModel(),
    };
    PaymentSettingsService.updateSettings(apiModel).subscribe({
      next: (response) => {
        if (response.result) {
          AccountSettingsStore.setGeneralSettings(generalSettings);
          getMerchantAccountsData();
          // getMerchantAccountsData() only refreshes this page's local copy.
          // The shared store holds the accounts every other screen reads their
          // payment settings from - item builders take their surcharge default
          // from it - and it is only fetched when empty, so without this it
          // keeps serving pre-save settings for the rest of the session.
          getMerchantAccountStore().fetchMerchantAccounts();
          ToastService.showSuccess("Payment settings saved successfully");
        }
      },
      error: () => {
        ToastService.showError("Payment settings save failed");
      },
    });
  }
  const handleToggleChange = (
    type: PaymentMethodKey,
    value: boolean,
    item: MerchantAccount,
  ) => {
    const updatedPaymentSettings = new PaymentSettings({
      ...item.paymentSettings,
      [type]: new PaymentMethodDetails({
        ...item.paymentSettings[type],
        isEnabled: value,
      }),
    });

    const updatedItem = new MerchantAccount({
      ...item,
      paymentSettings: updatedPaymentSettings,
    });

    setData((prevData) =>
      prevData.map((merchantAccount) =>
        merchantAccount.id === item.id ? updatedItem : merchantAccount,
      ),
    );
  };

  // const handleInputChange = (
  //   key: keyof MerchantAccount,
  //   value: string,
  //   id: string
  // ) => {
  //   setData((prevData) =>
  //     prevData.map((merchantAccount) =>
  //       merchantAccount.id === id
  //         ? new MerchantAccount({ ...merchantAccount, [key]: value })
  //         : merchantAccount
  //     )
  //   );
  // };

  const handleSurchargeChange = (
    type: PaymentMethodKey,
    value: string,
    item: MerchantAccount,
    isBlur?: boolean,
  ) => {
    if (isBlur) {
      // An empty or half-typed value must stay as the user left it rather than
      // becoming the string "NaN". validate() already flags it via isNaN, so
      // leaving the field blank surfaces the range message instead of saving.
      const parsed = parseFloat(value);
      value = isNaN(parsed) ? "" : parsed.toFixed(2);
    }
    const updatedPaymentSettings = new PaymentSettings({
      ...item.paymentSettings,
      [type]: new PaymentMethodDetails({
        ...item.paymentSettings[type],
        surchargePercentage: value,
      }),
    });

    const updatedItem = new MerchantAccount({
      ...item,
      paymentSettings: updatedPaymentSettings,
    });

    setData((prevData) =>
      prevData.map((merchantAccount) =>
        merchantAccount.id === item.id ? updatedItem : merchantAccount,
      ),
    );
  };

  /* Updates any of the surcharge flags of a payment method while keeping the
     rest of the merchant account untouched. */
  const updatePaymentMethod = (
    type: PaymentMethodKey,
    changes: Partial<PaymentMethodDetails>,
    item: MerchantAccount,
  ) => {
    const updatedItem = new MerchantAccount({
      ...item,
      paymentSettings: new PaymentSettings({
        ...item.paymentSettings,
        [type]: new PaymentMethodDetails({
          ...item.paymentSettings[type],
          ...changes,
        }),
      }),
    });

    setData((prevData) =>
      prevData.map((merchantAccount) =>
        merchantAccount.id === item.id ? updatedItem : merchantAccount,
      ),
    );
  };

  /* "Add surcharge" checkbox. Keeps the entered percentage visible when
     unchecked - the API model sends 0 in that case. */
  const handleAddSurchargeChange = (
    type: PaymentMethodKey,
    checked: boolean,
    item: MerchantAccount,
  ) => {
    const currentValue = parseFloat(
      item.paymentSettings[type].surchargePercentage,
    );
    updatePaymentMethod(
      type,
      {
        surchargeEnabled: checked,
        surchargePercentage:
          checked && !(currentValue > 0)
            ? PaymentMethodDetails.defaultSurchargePercentage
            : item.paymentSettings[type].surchargePercentage,
      },
      item,
    );
  };

  const handleAskCustomerFirstChange = (
    type: PaymentMethodKey,
    checked: boolean,
    item: MerchantAccount,
  ) => {
    updatePaymentMethod(type, { surchargeAskCustomerFirst: checked }, item);
  };

  /* Stepper arrows of the percentage field, clamped to the allowed range. */
  const handleSurchargeStep = (
    type: PaymentMethodKey,
    step: number,
    item: MerchantAccount,
  ) => {
    const { minSurchargePercentage, maxSurchargePercentage } =
      PaymentMethodDetails;
    const currentValue =
      parseFloat(item.paymentSettings[type].surchargePercentage) || 0;
    const nextValue = Math.min(
      maxSurchargePercentage,
      Math.max(minSurchargePercentage, currentValue + step),
    );
    updatePaymentMethod(
      type,
      { surchargePercentage: nextValue.toFixed(2) },
      item,
    );
  };

  // const onSaveName = () => {
  //   if (editDetails.id && editDetails.value) {
  //     handleInputChange(
  //       "statementDescriptor",
  //       editDetails.value,
  //       editDetails.id
  //     );
  //     setEditDetails((details) => ({
  //       ...details,
  //       isEdit: false,
  //       value: "",
  //     }));
  //   }
  // };

  const renderDetails = (
    item: MerchantAccount,
    index: number,
    isOpened: boolean,
  ) => {
    const renderPaymentRow = (
      key: PaymentMethodKey,
      label: string,
      logos: string[],
      isEnabled: boolean,
      isDisabled: boolean,
      paymentIconClass?: string,
    ) => {
      return (
        <FlexTable key={key} className="pd-height-static">
          <FlexTableDescription>
            <FlexContainer alignItems="center">
              <ToggleSwitch
                on={isEnabled}
                onChange={(value) => handleToggleChange(key, value, item)}
                disabled={isDisabled}
                height={10}
                offColor="#FF4B55"
              />
              <img
                src={PaymentCardIcon}
                alt={label}
                className="payment-method-icon"
              />
              <PaymentLabel>{label}</PaymentLabel>
              <PaymentIcons className={paymentIconClass}>
                {logos.map((logo, i) => (
                  <img key={i} src={logo} alt={label} />
                ))}
              </PaymentIcons>
            </FlexContainer>
          </FlexTableDescription>
        </FlexTable>
      );
    };

    /* Surcharge (Default) card - the default surcharge applied to new Purchase
       Links, Checkouts and Fundraisers created for this merchant account.
       Only Credit Card ("cards") is wired up today since it is the only
       payment method currently supported. */
    const renderSurchargeCard = (key: PaymentMethodKey) => {
      const paymentMethod: PaymentMethodDetails = item.paymentSettings[key];
      const hasError = item.hasError(key);
      const {
        minSurchargePercentage,
        maxSurchargePercentage,
        surchargeRangeMessage,
      } = PaymentMethodDetails;
      const currentValue = parseFloat(paymentMethod.surchargePercentage) || 0;
      const canStepUp =
        paymentMethod.surchargeEnabled &&
        currentValue < maxSurchargePercentage;
      const canStepDown =
        paymentMethod.surchargeEnabled &&
        currentValue > minSurchargePercentage;

      return (
        <SurchargeCard>
          <FlexContainer fxLayout="column" spacing={14}>
            <div>
              <FlexContainer alignItems="center">
                <CustomLabel
                  fontSize="16px"
                  fontWeight={600}
                  color="#2c3e50"
                  lineHeight="20px"
                >
                  Surcharge (Default)
                </CustomLabel>
                <InfoIcon />
              </FlexContainer>
            </div>
            <CustomLabel fontSize="14px" lineHeight="18px" color="#7a8699">
              Apply as the default surcharge for new Purchase Links, Checkouts,
              and Fundraisers.
            </CustomLabel>
            <FlexContainer alignItems="center" spacing={12}>
              <Input
                type="checkbox"
                id={`add-surcharge-${item.id}-${key}`}
                label="Add surcharge"
                value={paymentMethod.surchargeEnabled}
                className="surcharge-chkbox"
                onChange={(e) =>
                  handleAddSurchargeChange(key, e.target.checked, item)
                }
              />
              <SurchargeNumberField
                $disabled={!paymentMethod.surchargeEnabled}
                className={hasError ? "has-error" : ""}
              >
                <Input
                  type="text"
                  value={paymentMethod.surchargePercentage || ""}
                  disabled={!paymentMethod.surchargeEnabled}
                  onChange={(e, isBlur) =>
                    handleSurchargeChange(key, e.target.value, item, isBlur)
                  }
                  placeholder="%"
                />
                <SurchargeStepper>
                  <i
                    className={`fa fa-angle-up ${
                      canStepUp ? "clickable" : "not-allowed"
                    }`}
                    onClick={() =>
                      canStepUp &&
                      handleSurchargeStep(key, minSurchargePercentage, item)
                    }
                  />
                  <i
                    className={`fa fa-angle-down ${
                      canStepDown ? "clickable" : "not-allowed"
                    }`}
                    onClick={() =>
                      canStepDown &&
                      handleSurchargeStep(key, -minSurchargePercentage, item)
                    }
                  />
                </SurchargeStepper>
              </SurchargeNumberField>
              <CustomLabel fontSize="15px" color="#3d4a5c">
                %
              </CustomLabel>
              <CustomLabel
                fontSize="15px"
                color="#7a8699"
                className="text-nowrap"
              >
                to every payment
              </CustomLabel>
            </FlexContainer>
            <Input
              type="checkbox"
              id={`ask-customer-first-${item.id}-${key}`}
              label="Ask customer first"
              value={paymentMethod.surchargeAskCustomerFirst}
              className="surcharge-chkbox"
              disabled={!paymentMethod.surchargeEnabled}
              onChange={(e) =>
                handleAskCustomerFirstChange(key, e.target.checked, item)
              }
            />
            <SurchargeValidationNote $hasError={hasError}>
              <i className="fa fa-shield" />
              <CustomLabel>{surchargeRangeMessage}</CustomLabel>
            </SurchargeValidationNote>
          </FlexContainer>
        </SurchargeCard>
      );
    };

    const handleToggle = (open: boolean) => {
      setOpenPaymentDetailsIndexes((prev) =>
        open ? [...prev, index] : prev.filter((i) => i !== index),
      );
    };

    return (
      <VariantWrapper className="category-block payment-details-block">
        <MerchantCard key={index}>
          <MerchantCardHeader onClick={() => handleToggle(!isOpened)}>
            <img src={CardIcon} className="collapsed-header-icon" alt="card" />
            <CustomLabel
              fontSize="18px"
              fontWeight={600}
              lineHeight="22px"
              color="#2c3e50"
              className="clickable"
            >
              {item.name}
            </CustomLabel>
            {item.isDefault && <DefaultBadge>Default</DefaultBadge>}
            <i
              className={`fa ${
                isOpened ? "fa-chevron-up" : "fa-chevron-down"
              } clickable`}
            />
          </MerchantCardHeader>
          <Collapsible isOpen={isOpened}>
            <MerchantCardBody>
              <CustomLabel
                fontSize="15px"
                fontWeight={600}
                lineHeight="20px"
                color="#2c3e50"
              >
                Payment Method
              </CustomLabel>
              {item.paymentSettings && (
                <FlexContainer
                  fxLayout="column"
                  spacing={20}
                  margin="14px 0px 0px"
                >
                  {renderPaymentRow(
                    "cards",
                    "Credit Card",
                    [VisaLogo, MasterCardLogo, DiscoverLogo, AmexLogo],
                    item.paymentSettings.cards.isEnabled,
                    true,
                  )}
                  {/* {renderPaymentRow(
                "applePay",
                "Apple Pay",
                [ApplePayLogo],
                item.paymentSettings.applePay.isEnabled,
                true,
              )}
              {renderPaymentRow(
                "googlePay",
                "Google Pay",
                [GooglePayLogo],
                item.paymentSettings.googlePay.isEnabled,
                true,
              )}
              {renderPaymentRow(
                "bankAch",
                "Bank / ACH",
                [BankIcon],
                item.paymentSettings.bankAch.isEnabled,
                true,
                "bank-ach-icon",
              )}
              {renderPaymentRow(
                "checkWire",
                "Check / Wire",
                [CheckWireIcon],
                item.paymentSettings.checkWire.isEnabled,
                true,
                "check-wire-icon",
              )} */}
                </FlexContainer>
              )}
              {item.paymentSettings && renderSurchargeCard("cards")}

              {/* <FlexContainer
              alignItems="center"
              spacing={20}
              margin="0px 0px 20px 0px"
            >
              <CustomLabel>Statement Descriptor</CustomLabel>
              <EditableField
                value={
                  (editDetails.id === item.id && editDetails.value) ||
                  item.statementDescriptor
                }
                isEdit={editDetails.isEdit && editDetails.id === item.id}
                onEditClick={() => {
                  if (!rolesAndPermissionStore.hasManagePermission("Edit"))
                    return;
                  setEditDetails({
                    ...editDetails,
                    isEdit: true,
                    value: item.statementDescriptor,
                    id: item.id,
                  });
                }}
                onSaveClick={onSaveName}
                onCancelClick={() =>
                  setEditDetails((details) => ({
                    ...details,
                    value: "",
                    isEdit: false,
                  }))
                }
                onChange={(e) =>
                  setEditDetails((details) => ({
                    ...details,
                    value: e.target.value,
                  }))
                }
                placeholder="Enter Name"
              />
            </FlexContainer> */}

              <FlexTable className="justify-center" margin="20px 0px 0px">
                {item.paymentSettings.googlePay.isEnabled ||
                  (item.paymentSettings.applePay.isEnabled && (
                    <NoteText>
                      NOTE: Apple Pay and Google Pay are not available for
                      recurring transactions.
                    </NoteText>
                  ))}
              </FlexTable>
            </MerchantCardBody>
          </Collapsible>
        </MerchantCard>
      </VariantWrapper>
    );
  };

  const onClickCancel = () => {
    setGeneralSettings(AccountSettingsStore.generalSettings);
    getMerchantAccountsData();
  };

  const handleSellerEdit = (data: SellerGridModel) => {
    setSellerPopup({
      showEdit: true,
      showNew: false,
      sellerId: data.id,
      showDelete: false,
    });
  };

  const handleSellerDelete = (data: SellerGridModel) => {
    setSellerPopup({
      showEdit: false,
      showNew: false,
      sellerId: data.id,
      showDelete: true,
    });
  };

  function columns() {
    const columns: ITableHeaders<SellerGridModel>[] = [
      {
        dataKey: "isActive",
        title: "Status",
        className: "table-col-10",
        cellClassName: "table-col-10",
        onCellRender: (data: SellerGridModel) => {
          return (
            <ActiveInactiveButton inActive={!data.isActive}>
              {data.isActive ? "Active" : "Inactive"}
            </ActiveInactiveButton>
          );
        },
      },
      {
        dataKey: "name",
        title: "Name",
        className: "table-col-20",
        cellClassName: "table-col-20",
        onCellRender: (data: SellerGridModel) => {
          return <CustomLabel>{data.name}</CustomLabel>;
        },
      },
      {
        dataKey: "address",
        title: "Location",
        className: "table-col-30",
        cellClassName: "table-col-30",
        onCellRender: (data: SellerGridModel) => {
          return (
            <CustomLabel className="overflow-ellipsis">
              {formatAddress(data.address)}
            </CustomLabel>
          );
        },
      },
      {
        dataKey: "used",
        title: "Used",
        className: "table-col-10",
        cellClassName: "table-col-10",
        onCellRender: (data: SellerGridModel) => {
          return <CustomLabel>{data.used ? "Used" : "Not Used"}</CustomLabel>;
        },
      },
      {
        dataKey: "name",
        title: "Actions",
        className: "table-col-10 text-center",
        cellClassName: "table-col-10 text-center",
        onCellClick: (e) => {
          e.stopPropagation();
        },
        onCellRender: (data: SellerGridModel) => {
          return (
            <FlexContainer
              alignItems="center"
              justifyContent="center"
              spacing={10}
            >
              <img
                src={EditIcon}
                alt="edit-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!rolesAndPermissionStore.hasManagePermission("Edit"))
                    return;
                  handleSellerEdit(data);
                }}
                className="img-15 clickable"
              />
              <img
                src={DeleteIcon}
                alt="delete-icon"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!rolesAndPermissionStore.hasManagePermission("Delete"))
                    return;
                  handleSellerDelete(data);
                }}
                className="img-15 clickable"
              />
            </FlexContainer>
          );
        },
      },
    ];
    return columns;
  }

  function formatAddress(address: AddressModel) {
    if (!address) return "";
    return `${addCommaInAddress(address.address1)}${addCommaInAddress(address.address2)}${addCommaInAddress(address.city)}${addCommaInAddress(address.state.name)}${address.country}`;
  }

  function addCommaInAddress(value: string) {
    if (!value) return "";
    return value + ", ";
  }

  if (showLoader || AccountSettingsStore.isLoadingInProgress)
    return <>{new Loader(true).loaderComponent}</>;
  const showTableHeader = sellerData.length > 0 ? true : false;
  return (
    <ItemDetailMainWrapper className="payment-settings-wrapper">
      <MainOuterCard padding="30px 40px" className="ps-main-outer-card">
        <FlexContainer fxLayout="column" spacing={30}>
          <GeneralSettings
            settings={generalSettings}
            updateSettings={(settings: GeneralSettingsModel) => {
              generalSettings.validate(true);
              setGeneralSettings(new GeneralSettingsModel({ ...settings }));
            }}
          />
          <Beneficiaries
            beneficiaries={
              AccountSettingsStore.beneficiaryService.beneficiaries || []
            }
            isLoading={
              AccountSettingsStore.beneficiaryService.isLoadingInProgress
            }
            countriesWithState={itemStore.countryStore.countriesWithState}
          />
          <Categories
            generalSettings={generalSettings}
            rolesAndPermissionStore={rolesAndPermissionStore}
            countriesWithState={itemStore.countryStore.countriesWithState}
            handleChange={(settings: GeneralSettingsModel) => {
              generalSettings.validate(true);
              setGeneralSettings(
                new GeneralSettingsModel({
                  ...settings,
                  categories: settings.categories,
                }),
              );
            }}
          />
          <CollapsiblePanel
            title="Payment Details"
            className="payment-detail-header"
            isOpened={openPaymentDetailsBlock}
            onToggle={(open) => setOpenPaymentDetailsBlock(open)}
            collapsibleClassName="full-width"
          >
            <FlexContainer justifyContent="flex-start" padding="10px 30px 20px">
              <CustomLabel textAlign="left">
                Payment Details can be modified for Active Merchant Accounts
                only.
              </CustomLabel>
            </FlexContainer>
            {data.map((item, index) =>
              renderDetails(
                item,
                index,
                openPaymentDetailsIndexes.includes(index),
              ),
            )}
          </CollapsiblePanel>
          <CollapsiblePanel
            title="Sellers"
            className="payment-detail-header"
            collapsibleClassName="full-width"
            isOpened={false}
          >
            <TableContainer className="default-table seller-table">
              <TableGrid
                data={sellerData}
                headers={showTableHeader ? columns() : ""}
                isShowPlaceholder={sellerData.length === 0 ? false : true}
                tableHeader={
                  <TableWithHeaderButtons
                    title="Purchase Link"
                    totalItems={20}
                    icon="uj"
                    isShowHeaderButton={true}
                    onNewButtonClick={() => {
                      if (
                        !rolesAndPermissionStore.hasManagePermission("Create")
                      )
                        return;
                      setSellerPopup({ ...sellerPopup, showNew: true });
                    }}
                    showRecentButton={false}
                    description="Create one or more Sellers with options and attribute purchases on Checkout Pages."
                  />
                }
                cellSpacing={20}
                //pagination={purchaseLinkStore.pagination}
                totalItemCount={20}
                countriesWithState={itemStore.countryStore.countriesWithState}
              />
              {/* {!showTableHeader && sellerData.length === 0 && (
                <BasicPlaceholder>
                  <CustomLabel>No Sellers found</CustomLabel>
                  <SapphireBlueButton
                    primary
                    onClick={() => onClickSave()}
                    height="40px"
                  >
                    Create New
                  </SapphireBlueButton>
                </BasicPlaceholder>
              )} */}
            </TableContainer>
          </CollapsiblePanel>
          <PromotionsGrid
            data={promotionsData}
            rolesAndPermissionStore={rolesAndPermissionStore}
            countriesWithState={itemStore.countryStore.countriesWithState}
            onRefresh={(refresh: boolean) => {
              if (refresh) {
                getPromotionDetails();
              }
            }}
          />
          <div className="full-width">
            <FlexContainer
              alignItems="center"
              spacing={30}
              justifyContent="center"
            >
              <SapphireBlueButton onClick={() => onClickCancel()} height="40px">
                Cancel
              </SapphireBlueButton>
              <SapphireBlueButton
                primary
                onClick={() => onClickSave()}
                height="40px"
              >
                Save
              </SapphireBlueButton>
            </FlexContainer>
          </div>
        </FlexContainer>
      </MainOuterCard>
      {sellerPopup.showNew && (
        <NewSellerPopup
          onClose={(refresh: boolean, sellerId: string) => {
            if (refresh) {
              getSellerDetails();
            }
            setSellerPopup({ ...sellerPopup, showNew: false });
          }}
          countryStore={itemStore.countryStore}
        />
      )}
      {sellerPopup.showEdit && (
        <NewSellerPopup
          isEdit
          sellerId={sellerPopup.sellerId}
          onClose={(refresh: boolean, sellerId: string) => {
            if (refresh) {
              getSellerDetails();
            }
            setSellerPopup({ ...sellerPopup, showEdit: false });
          }}
          countryStore={itemStore.countryStore}
        />
      )}
      {sellerPopup.showDelete && (
        <DeleteSellerPopup
          sellerId={sellerPopup.sellerId}
          onClose={(refresh: boolean) => {
            if (refresh) {
              getSellerDetails();
            }
            setSellerPopup({ ...sellerPopup, showDelete: false });
          }}
          sellerData={sellerData.find(
            (seller) => seller.id === sellerPopup.sellerId,
          )}
        />
      )}
    </ItemDetailMainWrapper>
  );
};

export default observer(PaySettingsPaymentMethods);
